import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { agentForgotPassword, agentResetPassword } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Shield, Phone, Lock, Eye, EyeOff, CheckCircle, KeyRound } from "lucide-react";

export default function AgentForgotPassword() {
  const navigate = useNavigate();
  const [step, setStep] = useState('mobile'); // 'mobile' | 'otp' | 'success'
  const [mobile, setMobile] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [devOtp, setDevOtp] = useState(null);

  const handleSendOTP = async (e) => {
    e.preventDefault();
    if (!/^\d{10}$/.test(mobile)) { toast.error("Enter a valid 10-digit mobile number"); return; }
    setLoading(true);
    try {
      const res = await agentForgotPassword(mobile);
      toast.success("OTP sent to your mobile number");
      // DEV ONLY: surface dev_otp to the screen only in non-production builds.
      if (process.env.NODE_ENV !== 'production' && res.data.dev_otp) setDevOtp(res.data.dev_otp);
      setStep('otp');
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send OTP");
    } finally { setLoading(false); }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!otp || otp.length !== 6) { toast.error("Enter the 6-digit OTP"); return; }
    if (newPassword.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (newPassword !== confirmPassword) { toast.error("Passwords do not match"); return; }
    setLoading(true);
    try {
      await agentResetPassword(mobile, otp, newPassword);
      toast.success("Password reset successful!");
      setStep('success');
    } catch (err) {
      toast.error(err.response?.data?.detail || "Password reset failed");
    } finally { setLoading(false); }
  };

  if (step === 'success') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#F8FAFC] to-[#EEF2FF] flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 sm:p-12 text-center max-w-md w-full">
          <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="h-12 w-12 text-green-600" />
          </div>
          <h2 className="text-2xl font-black text-[#0A2540] mb-3" style={{fontFamily:'Outfit, sans-serif'}}>Password Reset Successful</h2>
          <p className="text-gray-600 text-sm leading-relaxed mb-8">
            Your password has been reset successfully. You can now log in with your new password.
          </p>
          <Button onClick={() => navigate('/agent/login')} className="w-full bg-[#1565C0] hover:bg-[#0D47A1] text-white rounded-full py-3 font-semibold">
            Go to Agent Login
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8FAFC] to-[#EEF2FF] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-br from-[#1565C0] to-[#00BCD4] rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
            <KeyRound className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-2xl font-black text-[#0A2540]" style={{fontFamily:'Outfit, sans-serif'}}>Reset Password</h1>
          <p className="text-sm text-gray-500 mt-1">
            {step === 'mobile' ? 'Enter your registered mobile number' : 'Enter OTP and set new password'}
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-6 sm:p-8">
          {step === 'mobile' ? (
            <form onSubmit={handleSendOTP} className="space-y-4">
              <div>
                <Label className="text-[10px] uppercase tracking-[0.2em] text-gray-500 font-bold mb-2 block">Mobile Number</Label>
                <div className="flex gap-2">
                  <span className="bg-gray-100 rounded-xl px-3 flex items-center text-sm text-gray-600 font-semibold">+91</span>
                  <Input 
                    data-testid="reset-mobile"
                    value={mobile} 
                    onChange={e => setMobile(e.target.value.replace(/\D/g, '').slice(0, 10))} 
                    placeholder="Enter 10-digit mobile" 
                    maxLength={10} 
                    className="rounded-xl" 
                  />
                </div>
                <p className="text-xs text-gray-400 mt-2">We'll send an OTP to verify your identity</p>
              </div>
              <Button type="submit" disabled={loading || mobile.length !== 10} className="w-full bg-[#1565C0] hover:bg-[#0D47A1] text-white rounded-full py-3 font-semibold mt-2">
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Send OTP"}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleResetPassword} className="space-y-4">
              {/* Dev mode OTP display */}
              {devOtp && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-center">
                  <p className="text-xs text-blue-600 font-semibold">Dev Mode OTP</p>
                  <p className="text-2xl font-mono font-bold text-blue-800 tracking-wider">{devOtp}</p>
                </div>
              )}

              <div>
                <Label className="text-[10px] uppercase tracking-[0.2em] text-gray-500 font-bold mb-2 block">OTP sent to +91 {mobile}</Label>
                <Input 
                  data-testid="reset-otp"
                  value={otp} 
                  onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))} 
                  placeholder="Enter 6-digit OTP" 
                  maxLength={6} 
                  className="rounded-xl text-center text-lg tracking-widest font-mono" 
                />
              </div>

              <div>
                <Label className="text-[10px] uppercase tracking-[0.2em] text-gray-500 font-bold mb-2 block">New Password (min 8 characters)</Label>
                <div className="relative">
                  <Input 
                    data-testid="reset-new-password"
                    type={showPassword ? "text" : "password"} 
                    value={newPassword} 
                    onChange={e => setNewPassword(e.target.value)} 
                    placeholder="Create new password" 
                    minLength={8} 
                    className="rounded-xl pr-10" 
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {newPassword && newPassword.length < 8 && (
                  <p className="text-xs text-red-500 mt-1">Password must be at least 8 characters</p>
                )}
              </div>

              <div>
                <Label className="text-[10px] uppercase tracking-[0.2em] text-gray-500 font-bold mb-2 block">Confirm Password</Label>
                <Input 
                  data-testid="reset-confirm-password"
                  type="password" 
                  value={confirmPassword} 
                  onChange={e => setConfirmPassword(e.target.value)} 
                  placeholder="Confirm new password" 
                  className="rounded-xl" 
                />
                {confirmPassword && newPassword !== confirmPassword && (
                  <p className="text-xs text-red-500 mt-1">Passwords do not match</p>
                )}
              </div>

              <Button 
                type="submit" 
                disabled={loading || otp.length !== 6 || newPassword.length < 8 || newPassword !== confirmPassword} 
                className="w-full bg-[#1565C0] hover:bg-[#0D47A1] text-white rounded-full py-3 font-semibold mt-2"
              >
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Reset Password"}
              </Button>

              <button 
                type="button" 
                onClick={() => { setStep('mobile'); setOtp(''); setNewPassword(''); setConfirmPassword(''); setDevOtp(null); }}
                className="w-full text-xs text-gray-500 hover:text-gray-700 py-2"
              >
                Change mobile number
              </button>
            </form>
          )}

          <div className="mt-6 pt-4 border-t border-gray-100 text-center">
            <button onClick={() => navigate('/agent/login')} className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1 mx-auto">
              <ArrowLeft className="h-3 w-3" /> Back to Agent Login
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
